import * as React from 'react';
import classNames from 'classnames';
import styles from './styles.module.scss';
import Icon, { IconsAvailable } from '@/components/ui/Icon';
import Image from 'next/image';
import wing from './effect-wing.svg';
import Tabs, { ITabItem } from '@/components/ui/Tabs';

export type WellTheme =
  | 'default'
  | 'hero'
  | 'tickets'
  | 'leaderboard'
  | 'faq'
  | 'faq-hero'
  | 'checkout'
  | 'transparent'
  | 'discrete'
  | 'filter';
export type WellBorderTheme = 'gold' | 'silver' | 'bronze' | 'discrete';

export interface IWellProps extends React.PropsWithChildren {
  id?: string;
  className?: string;
  theme?: WellTheme;
  title?: string;
  titleIcon?: IconsAvailable;
  effects?: {
    glow?: boolean;
    stripe?: 'blue' | 'gold';
    lines?: boolean;
    wings?: boolean;
    circles?: boolean;
    icon?: IconsAvailable;
    imageUrl?: string;
    imageWidth?: number;
    backgroundImageUrl?: string;
    border?: WellBorderTheme;
  };
  tabs?: ITabItem[];
}

const Well: React.FunctionComponent<IWellProps> = (props) => {
  const imageWidth = props.effects?.imageWidth || 100;
  return (
    <div
      className={classNames(
        styles['well'],
        {
          [styles['theme-hero']]: props.theme === 'hero',
          [styles['theme-tickets']]: props.theme === 'tickets',
          [styles['theme-leaderboard']]: props.theme === 'leaderboard',
          [styles['theme-faq']]: props.theme === 'faq',
          [styles['theme-faq-hero']]: props.theme === 'faq-hero',
          [styles['theme-checkout']]: props.theme === 'checkout',
          [styles['theme-transparent']]: props.theme === 'transparent',
          [styles['theme-discrete']]: props.theme === 'discrete',
          [styles['theme-filter']]: props.theme === 'filter',
          [styles['has-image']]: props.effects?.imageUrl,
          [styles['has-effects']]: props.effects,
          [styles['has-effects-image']]: props.effects?.imageUrl,
          [styles['has-border']]: props.effects?.border,
          [styles['has-border-gold']]: props.effects?.border === 'gold',
          [styles['has-border-silver']]: props.effects?.border === 'silver',
          [styles['has-border-bronze']]: props.effects?.border === 'bronze',
          [styles['has-border-discrete']]: props.effects?.border === 'discrete',
        },
        props.className,
      )}
      id={props.id}
    >
      {props.title && (
        <div className={styles['well__title']}>
          <div className={styles['well__title__wrapper']}>
            {props.titleIcon && (
              <Icon
                width={16}
                height={16}
                kind={props.titleIcon}
              />
            )}
            <div className={styles['well__title__label']}>{props.title}</div>
          </div>
        </div>
      )}
      {props.effects && (
        <div
          className={classNames(styles['well__effects'], {
            [styles['has-glow']]: props.effects.glow,
            [styles['has-stripe']]: !!props.effects.stripe,
            [styles['stripe-blue']]: props.effects.stripe === 'blue',
            [styles['stripe-gold']]: props.effects.stripe === 'gold',
          })}
        >
          {props.effects.circles && (
            <Icon
              width={132}
              height={132}
              kind="effect-circles"
              className={styles['well__circles']}
            />
          )}
          {(props.effects.imageUrl || props.effects.icon) && (
            <div
              className={styles['well__effects-image']}
              style={{
                width: props.effects.imageWidth,
              }}
            >
              {props.effects.imageUrl && (
                <Image
                  src={props.effects.imageUrl}
                  fill={true}
                  sizes={`${imageWidth}px`}
                  alt=""
                />
              )}
              {props.effects.icon && (
                <Icon
                  kind={props.effects.icon}
                  width={100}
                  height={150}
                />
              )}
            </div>
          )}
          {props.effects.lines && (
            <div className={styles['well__effects-lines']}>
              <div className={styles['well__effects-lines__a']} />
              <div className={styles['well__effects-lines__b']} />
            </div>
          )}
          {props.effects.wings && (
            <div className={styles['well__effects-wings']}>
              <Image
                alt=""
                width={439}
                height={55}
                priority={false}
                src={wing}
              />
              <Image
                alt=""
                width={439}
                height={55}
                priority={false}
                src={wing}
              />
            </div>
          )}
        </div>
      )}
      <div className={styles['well__bg']}>
        <div>
          {props.effects?.backgroundImageUrl && (
            <div className={styles['well__bg__image']}>
              <Image
                alt=""
                priority={false}
                src={props.effects.backgroundImageUrl}
                fill={true}
                sizes="100vw"
              />
            </div>
          )}
        </div>
      </div>
      {props.tabs && props.tabs.length > 0 && <Tabs items={props.tabs} />}
      <div className={styles['well__content']}>{props.children}</div>
    </div>
  );
};

Well.displayName = 'Well';

export default Well;
